import { useQuery, useMutation, useQueryClient } from 'react-query';
import ApiService from '@/services/api';
import { IStoredVoiceMessage, IPendingCountResponse } from '@/types/voicemessaging';

// Query keys
export const voiceMessageKeys = {
  all: ['voiceMessages'] as const,
  list: () => [...voiceMessageKeys.all, 'list'] as const,
  count: () => [...voiceMessageKeys.all, 'count'] as const,
  onlineUsers: () => ['onlineUsers'] as const,
};

// Hook to fetch pending voice messages
export const useVoiceMessages = () => {
  return useQuery<IStoredVoiceMessage[], Error>({
    queryKey: voiceMessageKeys.list(),
    queryFn: async () => {
      try {
        return await ApiService.getVoiceMessages();
      } catch {
        // Return empty array if voice messaging is unavailable (Redis not running)
        return [];
      }
    },
    refetchInterval: 30000, // Check for new messages every 30 seconds
    refetchOnWindowFocus: true,
    staleTime: 10000, // Consider data stale after 10 seconds
    retry: false,
  });
};

// Hook to fetch voice message count
export const useVoiceMessageCount = () => {
  return useQuery<IPendingCountResponse, Error>({
    queryKey: voiceMessageKeys.count(),
    queryFn: async () => {
      try {
        return await ApiService.getVoiceMessageCount();
      } catch {
        // Return zero counts if voice messaging is unavailable (Redis not running)
        return { total: 0, unplayed: 0 };
      }
    },
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    staleTime: 10000,
    retry: false,
  });
};

// Hook to delete a voice message
export const useDeleteVoiceMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => ApiService.deleteVoiceMessage(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: voiceMessageKeys.all });
    },
  });
};

// Hook to mark a voice message as played
export const useMarkVoiceMessagePlayed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => ApiService.markVoiceMessagePlayed(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: voiceMessageKeys.all });
    },
  });
};

// Hook to store a voice message (for offline users)
export const useStoreVoiceMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { to: string; audio_base64: string; duration: number }) =>
      ApiService.storeVoiceMessage(data),
    onSuccess: () => {
      // Optionally invalidate queries if needed
      queryClient.invalidateQueries({ queryKey: voiceMessageKeys.all });
    },
  });
};

// Hook to fetch online users
export const useOnlineUsers = () => {
  return useQuery({
    queryKey: voiceMessageKeys.onlineUsers(),
    queryFn: async () => {
      try {
        return await ApiService.getOnlineUsers();
      } catch {
        // Return empty array if WebRTC is unavailable (Redis not running)
        return [];
      }
    },
    refetchInterval: 10000, // Refresh every 10 seconds
    staleTime: 5000,
    retry: false,
  });
};

export default useVoiceMessages;
